import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  CircularProgress,
} from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import PaymentModeSummaryReportService from 'services/PaymentModeSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function PaymentModeSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    mode: tCommon('Payment Mode'),
    debit: {
      title: tCommon('Debit'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.debit || 0);
      },
    },
    credit: {
      title: tCommon('Credit'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.credit || 0);
      },
    },
    balance: {
      title: tCommon('Balance'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.balance || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson
    );
  }, [
    filterFromDate,
    filterToDate,
    filterTerminal,
    filterSalesperson,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
  ]);

  useEffect(() => {
    // getMachineName();
    getSalespersons();
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    generateSummeryReport();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Payment Mode Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Debit')]: `${shop?.currency || ''} ${summary.totalDebit}`,
        [tCommon('Credit')]: `${shop?.currency || ''} ${summary.totalCredit}`,
        [tCommon('Balance')]: `${shop?.currency || ''} ${summary.totalBalance}`,
      }
    );
  };

  const generateSummeryReport = () => {
    const { startDate, endDate } = dynamicDateRange('PAYMENT_MODE_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadPaymentSummaryReports({
      startTime: startDate,
      endTime: endDate,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterSalesperson,
      filterTerminal,
    });
  };

  const loadPaymentSummaryReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = new Date(startTime.getTime() - startTime.getTimezoneOffset() * 60000);
      const timezoneFreeEndTime = new Date(endTime.getTime() - endTime.getTimezoneOffset() * 60000);

      const data = {
        filter_start_time: timezoneFreeStartTime,
        filter_end_time: timezoneFreeEndTime,
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
      };
      const resp = await PaymentModeSummaryReportService.getPaymentModeSummaryReport(data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setTimeout(() => setLoadingIndicator(false), 300);
    }
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };
  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Payment Summary Report for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getBillReportSummary(filteredItems);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'mode':
          return tCommon('Total');
        case 'debit':
          return summary.totalDebit;
        case 'credit':
          return summary.totalCredit;
        case 'balance':
          return summary.totalBalance;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PAYMENTS-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to csv successfully'),
      type: 'success',
    });
  };

  const _getBillReportSummary = (items) => {
    let totalDebit = 0;
    let totalCredit = 0;
    let totalBalance = 0;
    (items || []).forEach((item) => {
      totalDebit += item?.debit || 0;
      totalCredit += item?.credit || 0;
      totalBalance += item?.balance || 0;
    });

    return {
      totalDebit: ShopsHelper.getAmountFormatted(shop, totalDebit),
      totalCredit: ShopsHelper.getAmountFormatted(shop, totalCredit),
      totalBalance: ShopsHelper.getAmountFormatted(shop, totalBalance),
    };
  };

  const headerData = [
    {
      label: tCommon('Payment Mode'),
      id: 'mode',
      type: 'text',
    },
    {
      label: tCommon('Debit'),
      id: 'debit',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item?.debit || 0)}`;
      },
    },
    {
      label: tCommon('Credit'),
      id: 'credit',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item?.credit || 0)}`;
      },
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item?.balance || 0)}`;
      },
    },
  ];

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Payment Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.dateDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="PAYMENT_MODE_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminals.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Salesperson')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterSalesperson}
                  onChange={(e) => setFilterSalesperson(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterSalesperson) => {
                    if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterSalesperson.map(
                      (id) => salesPersons.find((person) => person.id === id).name
                    );
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {salesPersons.map((salesperson) => (
                    <MenuItem key={salesperson.id} value={salesperson.id}>
                      <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                      <ListItemText primary={salesperson?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummeryReport}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <>
            <Info
              severity="warning"
              content={tReport(
                'The payment report excludes expenses and the amounts may vary from the shift report in the POS app. Data is available from {{date}}'
              )}
            />
            <div className={styles.tableContainer} style={{ marginTop: 20 }}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={filteredItems ? filteredItems : items}
                rowKey="id"
                summary={{
                  mode: tCommon('Total'),
                  debit: `${shop?.currency || ''} ${summary.totalDebit}`,
                  credit: `${shop?.currency || ''} ${summary.totalCredit}`,
                  balance: `${shop?.currency || ''} ${summary.totalBalance}`,
                }}
              />
            </div>
          </>
        ) : (
          !loadingIndicator && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )
        ))
      )}
      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(PaymentModeSummaryReport);
